import { Injectable } from '@angular/core';
import { Validators, FormGroup, FormBuilder } from '@angular/forms';
import { QuestionBase } from 'src/app/models/question-base';
import { DropdownQuestion } from 'src/app/models/question-dropdown';
import { RadioQuestion } from 'src/app/models/question-radio';
import { YesNoQuestion } from 'src/app/models/question-yesno';
import { CheckboxQuestion } from 'src/app/models/question-checkbox';
import { TextboxQuestion } from 'src/app/models/question-textbox';
import { TextareaQuestion } from 'src/app/models/question-textarea';
import { DatepickerQuestion } from 'src/app/models/question-datepicker';
import { CompoundQuestion } from 'src/app/models/question-compound';
import { AutocompleteQuestion } from 'src/app/models/question-autocomplete';
import { FilepickerQuestion } from 'src/app/models/question-filepicker';

// utilities
import * as luxon from 'luxon';
import { assign, clone, cloneDeep, compact, pick, isArray, get, filter, find, forEach, map, reduce, isString, set, trim, keyBy } from 'lodash';
import t from 'typy';

// App State
import { Store } from '@ngrx/store';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { AppState } from '../app-reducer';

// Models
import { optionsMap } from 'src/app/models/options-map';
import { customFormTypes, skippedQuestions } from 'src/app/models/data-model';
import {
    validateStateEligible,
    validateAge,
    validateMinAge,
    validateDate,
    validateDateShort,
    validateDateShortFuture,
    validateFutureDate,
    validateEmail,
    validate2YrsFutureDate,
    validate10YrsPastDate,
    validate5YrsPastDateShort,
    validate5YrsPastDateLong,
    validate3YrsPastDateShort,
    validate2YrsPastDate,
    validate1YrPastDate,
    validate1YrFutureDate,
    validateName,
    validatePercentage,
    validatePhone,
    validateSS,
    validateTaxId,
    validateWeightAmount,
    validateHeightInches,
    validateHeightFeet,
    validateWeight,
    validateNumber,
    validateDriverLicense,
    validatePercentageAllocation,
    validateTermAmount,
    validateSupplementalFaceAmount,
    validateFlexiblePremiumIncreaseBy,
    validateSupplementYears,
    validateAutoTransfer,
    validateUSBankRoutingNumber,
    validateDeadEnd,
    validateCurrency,
    validateExistingPolicy,
    validateBillingMethod,
    validateAutoTransferAmt,
    validate1YrShortPastDate,
    validatePercentageNoZero,
    validateMaxAge,
    validateMaxIllustrationYears,
    validateThreeDigitNumber,
    validateInt32,
    multipleCheckboxRequired,
    validateFreeform,
    validateFreeformVelogica,
    validateHundredTotal,
    validateSignatureMatch,
    validate4digitPastYear,
    validateInt32Required,
    validateNoAllSpace,
    validateZip,
    validateOwnerAmount,
    validatePastDate

} from 'src/app/validators';
@Injectable({
  providedIn: 'root'
})
export class QuestionService {
  appState$: Observable<any>;
  appStateSubscription: Subscription;
  optionsMap: any = optionsMap;
  optionsData: any = null;
  homeState: string;
  vulFinalTotal: any = 0;
  vulAccumulationTotals = [];
  flaggedVulAccumulationTotals = [];
  vulSegmentProceedsAllocationTotals = [];
  vulSegmentProceedsAllocationFinalTotal: any = 0;
  BaseHighParCappedIndexedAccountTotals = [];
  BaseHighParCappedIndexedAccountFinalTotal: any = 0;
  //JIRA: 3476 Begin
  BaseCappedTwoYearIndexedAccountTotals = [];
  BaseCappedTwoYearIndexedAccountFinalTotal: any = 0;
  HighCappedIndexedAccountTotals = [];
  HighCappedIndexedAccountFinalTotal: any = 0;
  //JIRA: 3476 End
  AllocationofMonthlyChargesTotals = [];
  AllocationofMonthlyChargesFinalTotal: any = 0;
  AccountBalancerTotals = [];
  AccountBalancerFinalTotal: any = 0;
  vulAlreadyZeroed = false;
  tableQuestions = [];
  maskCurrency= 'mask-currency';
  validatorLookup: any = {
    'required'            : Validators.required,
    'validateState'       : validateStateEligible,
    'name'                : validateName,
    'phone'               : validatePhone,
    'email'               : validateEmail,
    'ss'                  : validateSS,
    'taxid'               : validateTaxId,
    'feet'                : validateHeightFeet,
    'inches'              : validateHeightInches,
    'weightloss'          : validateWeightAmount,
    'weight'              : validateWeight,
    'lessThan1Yr'        :  validate1YrFutureDate,
    'lessThan2'           : validate2YrsFutureDate,
    'moreThan10'          : validate10YrsPastDate,
    'moreThan5MonthYear'  : validate5YrsPastDateShort,
    'moreThan5FullDate'   : validate5YrsPastDateLong,
    'moreThan3MonthYear'  : validate3YrsPastDateShort,
    'moreThan2'           : validate2YrsPastDate,
    'moreThan1'           : validate1YrPastDate,
    'moreThan1ShortDate'  : validate1YrShortPastDate,
    'age'                 : validateAge,
    'minAge'              : validateMinAge,
    'date'                : validateDate,
    'dateShort'           : validateDateShort,
    'dateShortFuture'     : validateDateShortFuture,
    'dateFuture'          : validateFutureDate,
    'percentage'          : validatePercentage,
    'isNumber'            : validateNumber,
    'license'             : validateDriverLicense,
    'termAmount'          : validateTermAmount,
    'supplementPercentage': validateFlexiblePremiumIncreaseBy,
    'supplementYears'     : validateSupplementYears,
    'autoTransferNum'     : validateAutoTransfer,
    'autoTransferAmt'     : validateAutoTransferAmt,
    'USBankRouting'       : validateUSBankRoutingNumber,
    'deadEnd'             : validateDeadEnd,
    'currency'            : validateCurrency,
    'SupplementAmount'    : validateSupplementalFaceAmount,
    'percentageNoZero'    : validatePercentageNoZero,
    'maxAge'              : validateMaxAge,
    'illustrationYears'   : validateMaxIllustrationYears,
    'threeDigit'          : validateThreeDigitNumber,
    'validateInt'         : validateInt32,
    'checkBoxMin'         : multipleCheckboxRequired(),
    'validateFreeform'    : validateFreeform,
    'validateForVelogica' : validateFreeformVelogica,
    'equals100'           : validateHundredTotal,
    'FinalSignature'      : validateSignatureMatch,
    'yearPastOrCurrent'   : validate4digitPastYear,
    'noAllSpace'          : validateNoAllSpace,
    'noZero'              : validateZip,
    'datePast'          : validatePastDate
  };

  longest: number = 0;
  //JIRA: 6482
  payload = {};
  menuData = [
    {
      "label": "Proposed Insured",
      "name": "SECTIONAProposedInsured",
      "path": "express/proposed-insured",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Policy Owner",
      "name": "SECTIONBPolicyOwner",
      "path": "express/policy-owner",
      "valid": true,
      "visible": true,
      "questions": []
    },
    {
      "label": "Partnership Statement",
      "name": "PartnershipStatement",
      "path": "express/partnership-statement",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": [
        ["SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"Business\"}", "OR", "SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerRelationship == \"BusinessPartner\"}"], "AND", ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsuredNo == TRUE"]
      ]
    },
    {
      "label": "Trust Certification",
      "name": "TrustCertification",
      "path": "express/trust-certification",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"ExistingTrust\"}"]
    },
    {
      "label": "Beneficiary Information",
      "name": "SECTIONCBeneficiaryInformation",
      "path": "express/beneficiary-information",
      "valid": true,
      "visible": true,
      "questions": []
    },
    {
      "label": "Coverage Details",
      "name": "SECTIONDCoverageDetails",
      "path": "express/coverage-details",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "LTCR Personal Worksheet",
      "name": "LTCRPersonalWorksheet",
      "path": "express/ltc-personal-worksheet",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONAProposedInsured.StateOfIssue == \"PA\"", "OR","SECTIONAProposedInsured.StateOfIssue == \"TX\"", "OR","SECTIONAProposedInsured.StateOfIssue == \"UT\"", "AND","SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"LongTermCareRider\"","AND", "SECTIONAProposedInsured.AgentAttestation != TRUE"]
    },
    {
      "label": "Pre-Auth Payment",
      "name": "RequestforPreAuthorizedPaymentPlan",
      "path": "express/pre-authorized-payment",
      "valid": true,
      "visible": true,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.BillingMethod == \"PreAuthorizedPaymentPlan\""]
    },
    {
      "label": "Critical Illness Rider",
      "name": "CriticalIllness",
      "path": "express/critical-illness",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"CriticalIllnessBenefitRider\""]
    },
    {
      "label": "IUL Allocations",
      "name": "IULPremandSegmentProceedsAllocations",
      "path": "express/proceeds-allocation",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.ProductName CONTAINS \"IUL\"", "OR", "SECTIONDCoverageDetails.ProductName CONTAINS \"Indexed\""]
    },
    {
      "label": "VUL Allocations",
      "name": "VULFundAllocation",
      "path": "express/vul-fund-allocation",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.ProductName CONTAINS \"VUL\"", "OR", "SECTIONDCoverageDetails.ProductName CONTAINS \"Variable\""]
    },
    {
      "label": "Statement Policy Cost",
      "name": "SupplementCostBenefitPA",
      "path": "express/supplement-cost-benefit-pa",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONAProposedInsured.StateOfIssue == \"PA\"", "AND", "SECTIONDCoverageDetails.ProductName CONTAINS \"Term\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Ash001\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Ebix001\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Vive001\""]
    },
    {
      "label": "Statement Policy Cost",
      "name": "SupplementCostBenefitME",
      "path": "express/supplement-cost-benefit-me",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONAProposedInsured.StateOfIssue == \"ME\"", "AND", "SECTIONDCoverageDetails.ProductName CONTAINS \"Term\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Ash001\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Ebix001\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Vive001\""]
    },
    {
      "label": "Illustration",
      "name": "IllustrationCertification",
      "path": "express/illustration-certification",
      "valid": true,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONAProposedInsured.PartnerID != \"Ash001\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Ebix001\"", "AND", "SECTIONAProposedInsured.PartnerID != \"Vive001\""]
    },
    {
      "label": "Purpose & Funding",
      "name": "SECTIONEPurposeFundingInformation",
      "path": "express/purpose-funding-information",
      "valid": true,
      "visible": true,
      "questions": []
    },
    {
      "label": "Existing Insurance",
      "name": "SECTIONFExistingReplacementPendingInsuranceInformation",
      "path": "express/existing-replacement-pending",
      "valid": true,
      "visible": true,
      "questions": []
    },
    {
      "label": "Temporary Insurance",
      "name": "SECTIONJTemporaryLifeInsuranceAgreementApplication",
      "path": "express/temporary-life-insurance",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Juvenile Insurance",
      "name": "SECTIONIJuvenileInsurance",
      "path": "express/juvenile-insurance",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) < 18"]
    },
    {
      "label": "Business Financial Supplement",
      "name": "FinancialSupplementforBusinessInsurance",
      "path": "express/supplement-financial-business",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONEPurposeFundingInformation.PurposeOfInsurance CONTAINS \"BusinessInsurance\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"Business\"}", "OR", "SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerRelationship IN [\"BusinessPartner\", \"Employer\"]}", "OR", "SECTIONCBeneficiaryInformation.BeneficiaryInformation.ANY{BeneficiaryRelationship IN [\"BusinessPartner\", \"Employer\"]}"]
    },
    {
      "label": "Taxpayer ID ",
      "name": "TIN",
      "path": "express/taxpayer-identification",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Personal Financial Supplement",
      "name": "SupplementFinancialPersonal",
      "path": "express/supplement-financial-personal",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": [
        ["!(SECTIONEPurposeFundingInformation.PurposeOfInsurance CONTAINS \"BusinessInsurance\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"Business\"}", "OR", "SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerRelationship IN [\"BusinessPartner\", \"Employer\"]}", "OR", "SECTIONCBeneficiaryInformation.BeneficiaryInformation.ANY{BeneficiaryRelationship IN [\"BusinessPartner\", \"Employer\"]}"], "AND", [
          ["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) <= 65", "AND", "SECTIONDCoverageDetails.FlexiblePremiumProductsBaseAmount + SECTIONDCoverageDetails.FlexiblePremiumProductsSupplementalAmount + SECTIONDCoverageDetails.TermFaceAmount) > 7500000"], "OR", ["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) >= 66", "AND", "AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) <= 79", "AND", "SECTIONDCoverageDetails.FlexiblePremiumProductsBaseAmount + SECTIONDCoverageDetails.FlexiblePremiumProductsSupplementalAmount + SECTIONDCoverageDetails.TermFaceAmount) >= 5000000"], "OR", ["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) >= 80", "AND", "SECTIONDCoverageDetails.FlexiblePremiumProductsBaseAmount + SECTIONDCoverageDetails.FlexiblePremiumProductsSupplementalAmount + SECTIONDCoverageDetails.TermFaceAmount) >= 1000000)"]
        ]
      ]
    },
    {
      "label": "Personal Information",
      "name": "SECTIONGPersonalInformation",
      "path": "express/personal-information",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Lifestyle Information",
      "name": "SECTIONHLifestyleInformation",
      "path": "express/lifestyle-information",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Avocation Questionnaire",
      "name": "SupplementAvocation",
      "path": "express/supplement-avocation",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONHLifestyleInformation.EngagedActivity NOT CONTAINS \"NoParticipation\""]
    },
    {
      "label": "Aviation Questionnaire",
      "name": "SupplementAviation",
      "path": "express/supplement-aviation",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONHLifestyleInformation.Pilot == TRUE"]
    },
    {
      "label": "Medical Overview",
      "name": "Part2SECTIONAGeneralInformation",
      "path": "express/part2-general-information",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Medications",
      "name": "Part2SECTIONBMedications",
      "path": "express/part2-medications",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Medical Conditions",
      "name": "Part2SECTIONCMedicalConditions",
      "path": "express/part2-medical-conditions",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Medical Condition Details",
      "name": "Part2SECTIONCMedicalConditions2",
      "path": "express/part2-medical-conditions-details",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["Part2SECTIONCMedicalConditions.HeartOrBloodVessel NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.EndocrineOrDiabetes NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.Cancer NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.BloodOrAutoimmuneDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.RespiratoryDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.NeurologicalDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.MentalHealthDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.GastrointestinalDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.BoneJointDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"", "OR", "Part2SECTIONCMedicalConditions.KidneyBladderBreastReproductive NOT CONTAINS \"NoneOfTheseApplyToMe\""]
    },
    {
      "label": "Medical Diagnostics",
      "name": "Part2SECTIONDMedicalDiagnostic",
      "path": "express/part2-medical-diagnostic",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Medical Personal Info",
      "name": "Part2SECTIONEPersonalInformation",
      "path": "express/part2-personal-information",
      "valid": null,
      "visible": true,
      "questions": []
    },
    {
      "label": "Alcohol and Drugs",
      "name": "SupplementAlcoholAndDrug",
      "path": "express/supplement-alcohol-and-drug",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["Part2SECTIONEPersonalInformation.AlcoholAdvisedDiscontinueOrTreatment == TRUE", "OR", "Part2SECTIONEPersonalInformation.DrugsCounselingTreatment == TRUE", "OR", "Part2SECTIONEPersonalInformation.CocaineHeroinAmphetaminesHallucinogens == TRUE", "OR", "Part2SECTIONEPersonalInformation.TranquilizersSedativesNarcoticsPrescriptions == TRUE"]
    },
    {
      "label": "LTC Rider Supplement",
      "name": "SupplementLTC",
      "path": "express/supplement-long-term-care",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"LongTermCareRider\"", "AND", "SECTIONAProposedInsured.StateOfIssue != \"CA\""]
    },
    {
      "label": "LTC Rider Supplement",
      "name": "SupplementLTCCA",
      "path": "express/supplement-long-term-care-ca",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"LongTermCareRider\"", "AND", "SECTIONAProposedInsured.StateOfIssue == \"CA\""]
    },
    {
      "label": "Chronic Illness Rider Supplement",
      "name": "SupplementDBCHR",
      "path": "express/supplement-dbchr",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"DefinedBenefitChronicIllness\""]
    },
    {
      "label": "Pre-Auth Payment",
      "name": "RequestforPreAuthorizedPaymentPlanInsured",
      "path": "express/pre-authorized-payment-plan",
      "valid": null,
      "visible": true,
      "questions": [],
      "conditionalClause": ["SECTIONDCoverageDetails.BillingMethod == \"PreAuthorizedPaymentPlan\""]
    },
    {
      "label": "Disclosures",
      "name": "Disclosures",
      "path": "express/disclosures",
      "valid": null,
      "visible": false,
      "questions": []
    },
    {
      "label": "Insured Declarations",
      "name": "DeclarationsInsured",
      "path": "express/declarations",
      "valid": null,
      "visible": false,
      "questions": []
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerA",
      "path": "express/declarations-owner-A",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerB",
      "path": "express/declarations-owner-B",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerC",
      "path": "express/declarations-owner-C",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerD",
      "path": "express/declarations-owner-D",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerE",
      "path": "express/declarations-owner-E",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerF",
      "path": "express/declarations-owner-F",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerG",
      "path": "express/declarations-owner-G",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerH",
      "path": "express/declarations-owner-H",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerI",
      "path": "express/declarations-owner-I",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Owner Declarations",
      "name": "DeclarationsOwnerJ",
      "path": "express/declarations-owner-J",
      "valid": null,
      "visible": false,
      "questions": [],
      "conditionalClause": ["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"", "OR", "SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]
    },
    {
      "label": "Agent Report",
      "name": "AgentReport",
      "path": "express/agent-report",
      "valid": true,
      "visible": false,
      "questions": []
    },
    {
      "label": "Transmittal",
      "name": "TransmittalBlock",
      "path": "express/transmittal",
      "valid": true,
      "visible": false,
      "questions": []
    },
    {
      "label": "Agent Declarations",
      "name": "DeclarationsAgent",
      "path": "express/declarations-agent",
      "valid": false,
      "visible": false,
      "questions": []
    }];

  constructor(
    private store: Store<AppState>,
    private builder: FormBuilder
  ) {
    this.appState$ = this.store.select('sections');
  }

  // generate question for state
  generateQuestions(items: any, dependencies: any, data: any = null) {
    // save options data
    this.optionsData = data;

    // properties to pick
    const pickProperties = [
      'QuestionName',
      'QuestionText',
      'QuestionTextAlt',
      'ParentQuestionName',
      'ChildQuestions',
      'QuestionType',
      'Order',
      'QuestionOptions',
      'ConditionalClause',
      'ConditionalDependents',
      'MultipleResponse',
      'MultipleResponseChild',
      'ToolTip',
      'controlType',
      'validators',
      'mask',
      'inputType',
      'IsOptional',
      'IsHeaderQuestion',
      'Units',
      'QuestionVariations',
      'DefaultValue',
      'DefaultValueConditional',
      'Id',
      'QuestionRestrictions',
      'MapToOther'
    ];

    const sections: any = items.map((section: any) => {
      const updatedQuestions = section.Questions.map((item: any) => {

        const update = {
          validators: this.getValidators(item),
          controlType: this.getControlType(item),
          MultipleResponseChild: item.ParentQuestionName ? this.isMultipleResponseChild(item, section) : false,
          mask: this.getMask(item),
          inputType: this.getInputType(item),
          QuestionRestrictions: this.cleanQuestionRestrictions(item)
        };

        const conditionalityUpdate = {
          QuestionOptions: this.getOptions(item, section, update.MultipleResponseChild),
          ConditionalClause: this.reformatConditionalClause(item, section, update.MultipleResponseChild),
          ConditionalDependents: this.getConditionalDepdendencies(item, section.Name, items, dependencies),
          QuestionVariations: item.QuestionVariations ? this.reformatQuestionVariations(item, section, update.MultipleResponseChild) : null
        };

        const assigned = { ...item, ...update, ...conditionalityUpdate };

        return pick(assigned, pickProperties);
      });

      // update questions
      section.Questions = this.formatQuestions(updatedQuestions);

      // console.log('section', section);

      return section;
    });

    return sections;
  }

  isMultipleResponseChild(item: any, section: any) {
    const parentQ = section.Questions.find(element => element.QuestionName === item.ParentQuestionName);
    if (parentQ.MultipleResponse) {
      return true;
    } else if (parentQ.ParentQuestionName) {
      return this.isMultipleResponseChild(parentQ, section);
    } else {
      return false;
    }
  }

  fullPathify(clause: string, section: any, MultipleResponseChild: boolean) {

    const parentKey = trim(clause.split(' ')[0]).replace(/[()]/g, '');

    if (parentKey.length > 3 && !parentKey.includes('.')) {
      if (section.Questions.find(element => element.QuestionName === parentKey.replace(/[!]/ig, '') && !MultipleResponseChild)) {
        const symbols = clause.substr(0, clause.search(/[A-Z]/ig));
        const newName = symbols + section.Name + '.' + parentKey.replace(/[!]/ig, '') + ' ';
        if (clause) {
          clause = clause.replace(/^([^\s]+)\s/ig, newName);
        }
      } else if (clause !== 'FALSE' && !MultipleResponseChild) {
        console.log('ConditionalClause fault:', clause);
      }
    }
    return clause;
  }

  conditionalClauseArrayCleanup(array: any, length: number, section: any, MultipleResponseChild: boolean) {

    if (typeof array === 'object') {
      if (length > 1) {
        for (let i = length - 1; i > -1; i--) {
          array[i] = this.conditionalClauseArrayCleanup(array[i], array[i].length, section, MultipleResponseChild);
        }
        return compact(array);
      } else if (length = 1) {
        if (typeof array[0] === 'object') {
          for (let i = array[0].length - 1; i > -1; i--) {
            array[0][i] = this.conditionalClauseArrayCleanup(array[0][i], array[0][i].length, section, MultipleResponseChild);
          }
        } else {
          array[0] = this.conditionalClauseArrayCleanup(array[0].toString(), array[0].length, section, MultipleResponseChild);
        }
        return array[0];
      }
    } else {
      let string = array.trim();
      if (string.charAt(0) === '(') {
        string = string.substr(1);
      }
      if (string.charAt(0) === '(') {
        string = string.substr(1);
      }
      if (string.charAt(string.length - 1) === ')') {
        string = string.substr(0, string.length - 1);
      }
      array = this.fullPathify(string.trim(), section, MultipleResponseChild);

      return array;
    }
  }

  reformatConditionalClause(item: any, section: any, MultipleResponseChild: boolean) {

    // sanitize: remove unneeded parens and extra spaces from ConditionalClause
    let clause = item.ConditionalClause ? item.ConditionalClause.replace(/\(\{/g, '{').replace(/\}\)/g, '}').replace(/\s{2,}/g, ' ') : null;

    if (clause) {
      // special handling for file upload type questions
      if (clause.includes('IllustrationUpload')) {
        clause = clause.replace(/IllustrationUpload\b/ig, 'IllustrationUpload.Response');
        clause = clause.replace(/IllustrationUploadProtectionTerm\b/ig, 'IllustrationUploadProtectionTerm.Response');
      }
    }

    if (!clause) {
      return;
    }

    let array;
    let negArray;

    if (clause.charAt(0) === '!') {
      // find fully negated clauses and split them into negArrays
      let cutClause = (' ' + clause).slice(1).trim();
      // const openParens = (cutClause.match(/[\s\(\!]\(/g)).length;
      // const closeParens = (cutClause.match(/\)/g)).length;
      cutClause = cutClause.replace(/[\!\s\(](\([^\(]*\))(?!\s\>|\<|\=)/ig, '');
      if (cutClause === '') {
        negArray = clause.substr(0, clause.length - 1).split(/\(/);
        clause = null;
      }
    }

    if (clause) {

      // split other complex clauses into arrays
      clause = clause.replace(/and\s\!\(/ig, 'AND !(!(');
      array = clause.split(/(\(\([^()+]*\)[^()+]*\))|(\(\(AGE_NEAREST\(SECTIONAProposedInsured\.DateofBirth\).*\)\))/ig);

      for (let i = array.length - 1; i >= 0; i--) {
        if (array[i]) {
          if (array[i].match(/(\(\()/g)) {
            array[i] = array[i].split(/\)\s(and|or)/ig);
          } else if (array[i].match(/\)\s(and|or)|(and|or)\s\(|(and|or)\s\!\(|^[\s]{0,1}(and|or)\s/ig)) {
            let temp = array[i].split(/\)\s(and|or)|(and|or)\s\(|(and|or)\s\!\(|^[\s]{0,1}(and|or)\s/ig);

            temp = compact(temp);
            for (let j = 0; j < temp.length; j++) {
              temp[j] = temp[j].replace(/((\!\(){2,2})/ig, '!(');
              if (j === 0) {
                array[i] = temp[j];
              } else {
                array.splice(i + j, 0, temp[j]);
              }
            }
          }
        }
      }

      array.forEach((x, i) => {
        if (typeof x === 'string') {
          array[i] = x.length > 3 ? x.split(/\s(and|or)\s(?![^{]+[}])/ig) : x;
        } else if (typeof x === 'object') {
          x.forEach((y, j) => {
            if (typeof y === 'string') {
              array[i][j] = y.length > 3 ? y.split(/\s(and|or)\s(?![^{]+[}])/ig) : y;
            } else if (typeof x === 'object') {
              y.forEach((z, k) => {
                if (typeof k === 'string') {
                  array[i][j][k] = k.length > 3 ? k.split(/\s(and|or)\s(?![^{]+[}])/ig) : k;
                } else {
                  console.log('too deep -> fix this', array);
                }
              });
            }
          });
        }
      });

      // cleanup
      array = compact(array);

      array = this.conditionalClauseArrayCleanup(array, array.length, section, MultipleResponseChild);
      // console.log('here is the array', array);

    }

    if (negArray) {
      array = negArray;
    }

    return array;
  }

  reformatQuestionVariations(item: any, section: any, MultipleResponseChild: boolean) {
    const cleaned = item.QuestionVariations;
    item.QuestionVariations.forEach((x, i) => {
      cleaned[i].ConditionalClause = x.ConditionalClause ? this.reformatConditionalClause(x, section, MultipleResponseChild) : null;
    });
    return cleaned;
  }

  getConditionalDepdendencies(item: any, sectionName: any, items: any, dependencies: any) {
    const qName = sectionName + '.' + item.QuestionName;
    const qDepdendencies = dependencies.find(x => x.UpstreamQuestion === qName);
    if (qDepdendencies) {
      let newArray = [];
      JSON.parse(qDepdendencies.DownstreamQuestionNames).forEach(dep => {
        const depSection = dep.split('.')[0];
        const depName = dep.split('.')[1];
        const qSection = items.find(sec => sec.Name === depSection);
        const qObj = qSection.Questions.find(q => q.QuestionName === depName);
        newArray.push({name: dep, order: qObj.Id})
      });
      newArray = newArray.sort((a, b) => {
        return a.order - b.order;
      });
      newArray = newArray.map(x => x.name);
      return newArray;
    } else {
      return null;
    }
  }

  getMask(item: any) {
    let mask = [];
    if (item.QuestionType === 'Phone') {
      mask = ['mask-phone'];
    } else if (item.QuestionType === 'SSN') {
      mask = ['mask-ss'];
    } else if (item.QuestionType === 'Integer' && item.Units === '$') {
      mask = [this.maskCurrency];
    } else if (item.Units === 'Years') {
      mask = ['mask-integer-year'];
    } else if (item.QuestionType === 'Decimal' && item.Units === '$') {
      mask = ['mask-currency-decimal'];
    } else if (item.QuestionType === 'Decimal' && item.Units === '%') {
      mask = ['mask-percent-decimal'];
    } else if (item.QuestionType === 'Integer') {
      mask = ['mask-integer'];
    } else {
      mask = get(this.optionsMap[item.QuestionName], 'mask') || [];
    }
    return mask;
  }

  getInputType(item: any) {

    let inputType = 'text';

    const emailType = [
      'BeneficiaryEmail',
      'EmailAddress',
      'NewBusinessFirmContactEmailAddress'
    ];

    // email type
    if (emailType.includes(item.QuestionName)) {
      inputType = 'email';
    }

    const hashTypes = {
      // Decimal : 'number',
      Phone: 'tel'
    };

    inputType = hashTypes[item.QuestionType] || inputType;

    return inputType;
  }

  cleanQuestionRestrictions(item: any) {
    if (item.QuestionRestrictions) {
      const newRules = {};
      forEach(item.QuestionRestrictions, restriction => {
        newRules[restriction.Stage] = {user: restriction.UserRole, access: restriction.QuestionRestrictionType}
      });
      return newRules;
    } else {
      return null;
    }
  }

  getControlType(item: any) {
    const controlTypes = {
      'Address'       : 'input',          // handled by custom form
      'BloodPressure' : 'input',          // handled by custom form
      'Compound'      : 'compound',
      'Country'       : 'dropdown',
      'Date'          : 'datepicker',     // MM/DD/YYYY
      'Decimal'       : 'input',          // need masking
      'FreeForm'      : 'input',
      'Integer'       : 'input',
      'Length'        : 'input',          // handled by custom form
      'MonthYear'     : 'datepicker',     // need masking
      'MultiChoice'   : 'dropdown',
      'MultiSelect'   : 'checkbox',
      'Name'          : 'input',
      'Phone'         : 'input',          // need masking
      'Physician'     : 'input',          // handled by custom form
      'SSN'           : 'input',
      'State'         : 'dropdown',
      'textbox'       : 'input',
      'Signature'     : 'input',
      'YesNo'         : 'yesno',
      'Label'         : 'textarea',
      'File'          : 'filepicker',
    };

    /**
     * check optionsMap for forceType property
     */
    const type = this.optionsMap[item.QuestionName] && get(this.optionsMap[item.QuestionName], 'forceType')
      ? get(this.optionsMap[item.QuestionName], 'forceType')
      : controlTypes[item.QuestionType];

    return type;
  }

  getOptions(item: any, section: any, MultipleResponseChild: boolean) {

    let options: any;

    if (item.QuestionType === 'YesNo') {
      options = get(this.optionsMap[item.QuestionType], 'options');

    } else if (this.optionsMap[item.QuestionName] && get(this.optionsMap[item.QuestionName], 'options')) {
      options = get(this.optionsMap[item.QuestionName], 'options');

    } else if (item.QuestionType === 'State') {
      options = this.optionsData.states;
    }
    else if (item.QuestionName === 'PrescriptionName') {
      options = this.optionsData.medications;
    }
    else if (item.QuestionType === 'Country') {
      const countryOptions = find(this.optionsData.countries, { CountryListName: item.CountryListName });
      options = countryOptions.CountryListItems.map((country: any) => {
        return {
          key: country.Name,
          value: country.Name
        };
      });

    } else if (isArray(item.QuestionOptions) && item.QuestionOptions.length) {

      item.QuestionOptions.forEach((x, i) => {
        // create ConditionalClause array
        if (x.ConditionalClause) {
          item.QuestionOptions[i].ConditionalClause = this.reformatConditionalClause(x, section, MultipleResponseChild);
        }
        if (x.QuestionOptionVariations) {
          for (let j = 0; j < x.QuestionOptionVariations.length; j++) {
            // create ConditionalClause array
            if (x.QuestionOptionVariations[j].ConditionalClause) {
              item.QuestionOptions[i].QuestionOptionVariations[j].ConditionalClause = this.reformatConditionalClause(x.QuestionOptionVariations[j], section, MultipleResponseChild);
            }
          }
        }
      });

      options = this.transformOptions(item);

    } else {
      options = item.QuestionOptions;
    }

    return options;
  }

  transformOptions(item) {

    const updatedOptions = item.QuestionOptions.map(option => {
      const keyValue = item.QuestionType === 'MultiChoice' ? option.Name : false;
      const update = {
        key: keyValue,
        value: option.DisplayName || option.Name
      };
      return { ...update, ...option };
    });

    return updatedOptions;
  }

  transformSuggestions(item) {

    if (!item.QuestionSuggestions) { return; }

    const updatedOptions = item.QuestionSuggestions.map(option => {
      const update = {
        key: option.Suggestion,
        value: option.Suggestion
      };
      return { ...update, ...option };
    });

    return updatedOptions;
  }

  // utility
  formatQuestions(rawQuestions: any) {

    const questions: QuestionBase<any>[] = rawQuestions.map((item: any) => {
      let itemReturned = null;

      switch (item.controlType) {
        case 'dropdown':
          itemReturned = new DropdownQuestion(item);
          break;
        case 'input':
          itemReturned = new TextboxQuestion(item);
          break;
        case 'textarea':
          itemReturned = new TextareaQuestion(item);
          break;
        case 'datepicker':
          itemReturned = new DatepickerQuestion(item);
          break;
        case 'radio':
          itemReturned = new RadioQuestion(item);
          break;
        case 'yesno':
          itemReturned = new YesNoQuestion(item);
          break;
        case 'checkbox':
          itemReturned = new CheckboxQuestion(item);
          break;
        case 'compound':
          itemReturned = new CompoundQuestion(item);
          break;
        case 'autocomplete':
          itemReturned = new AutocompleteQuestion(item);
          break;
        case 'filepicker':
          itemReturned = new FilepickerQuestion(item);
          break;
      }

      return itemReturned;
    });

    // console.log('section questions', questions);

    return questions;
  }

  // utility: masks
  activateMasks(question: any, units?: any) {
    let mask = null;

    switch (question) {
      case 'mask-date':
        mask = [/[0-1]/, /[0-9]/, '/', /[0-3]/, /[0-9]/, '/', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];

        break;

      case 'mask-date-short':
        mask = [/[0-1]/, /[0-9]/, '/', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];

        break;

      case 'mask-ss':
        mask = {
          delimiter: '-',
          blocks: [3, 2, 4],
          numericOnly: true
        };

        break;

      case 'mask-tid':
        mask = {
          delimiter: '-',
          blocks: [2, 8],
          numericOnly: true
        };

        break;

      case 'mask-length-2':
        mask = {
          numericOnly: true,
          blocks: [2],
        };

        break;

      case ('mask-currency-decimal'):
        mask = {
          prefix: '$',
          numericOnly: true,
          numeral: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: '2',
          numeralPositiveOnly: true
        };

        break;

      case ('mask-percent-decimal'):
        mask = {
         // prefix: '%',
          numericOnly: true,
          numeral: true,
          numeralDecimalScale: '2',
          numeralPositiveOnly: true
        };

        break;

      case this.maskCurrency:
        mask = {
          prefix: '$',
          numericOnly: true,
          numeral: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: '0',
          numeralPositiveOnly: true
        };

        break;

      case 'mask-currency-no-symbol':
        mask = {
          numericOnly: true,
          numeral: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: '0',
          numeralPositiveOnly: true
        };

        break;

      case 'mask-integer':
        mask = {
          numericOnly: true,
          numeral: true,
          numeralThousandsGroupStyle: 'none',
          numeralDecimalScale: '0'
        };

        break;

      case 'mask-phone':
        mask = {
          delimiter: '-',
          blocks: [3, 3, 4],
          numericOnly: true
        };

        break;

      case 'mask-integer-year':
        mask = {
          numericOnly: true,
          numeral: true,
          blocks: [4],
          numeralThousandsGroupStyle: 'none',
          numeralDecimalScale: '0'
        };

        break;
    }

    return mask;
  }

  // utility: validation define validators array for question
  generateValidators(question: any, isRequired: boolean = false) {
    let validators = this.getValidators(question);
    if (isRequired && validators.indexOf('required') === -1) {
      validators = [...validators, 'required'];
    }

    const validationArray = validators ? validators.map((item: any) => this.validatorLookup[item]) : [];

    // console.log('validationArray', question.QuestionName, validationArray);

    return validationArray;
  }

  // utility: validation validation array
  getValidators(item: any) {

    // console.log('getValidators for', item.QuestionName, 'type is', item.QuestionType);

    // additional validatiors
    const additional = get(this.optionsMap[item.QuestionName], 'validators') || [];

    // numeric
    const numeric = ((item.QuestionType === 'Integer') && (item.Units !== '$') && (get(this.optionsMap[item.QuestionName], 'mask') === undefined)) ? ['isNumber'] : [];

    // Int32
    const integer = (item.QuestionType === 'Integer') ? ['validateInt'] : [];

    // date
    const date = (item.QuestionType === 'Date' && !additional.includes('dateFuture')) ? ['date'] : [];

    // date
    const dateShort = (item.QuestionType === 'MonthYear' && !additional.includes('dateShortFuture')) ? ['dateShort'] : [];

    // required
    let required = this.getRequired(item) ? ['required'] : [];

    // if checkboxes, update required to multiple check
    const isCheckboxes = (item.QuestionType === 'MultiSelect' && !item.IsOptional);
    required = isCheckboxes ? ['checkBoxMin'] : required;

    // currency : units having $
    const currency = ((item.Units === '$' || (get(this.optionsMap[item.QuestionName], 'mask') === this.maskCurrency)) && item.IsOptional !== true) ? ['currency'] : [];

    const validators = [...required, ...additional, ...numeric, ...integer, ...date, ...dateShort, ...currency];

    // console.log('validators are', validators);

    return validators;
  }

  // utility: custom validators (improve)
  getCustomValidator(questionName: string = 'default', data: any = {}) {
    const questionMap = {
      Street: [Validators.required, Validators.maxLength(100)],
      Street2: [Validators.maxLength(100)],
      City: [Validators.required, Validators.maxLength(30)],
      PrimaryResidenceState: [Validators.required],
      Zip: [Validators.required, Validators.minLength(5), Validators.maxLength(5)],
      Name: [Validators.required, Validators.maxLength(50), validateName],
      Phone: [Validators.required, validatePhone],
      Title: [],
      Suffix: [],
      ExistingPolicyToBeReplaced: [Validators.required, validateExistingPolicy(data.ReplaceExistingInsuranceOrUseFunds)],
      TempAgreementWanted: [Validators.required, validateBillingMethod(data.BillingMethod)],
      checkBoxMin: [Validators.required, multipleCheckboxRequired()],
      default: [Validators.required]
    };

    if (!questionName) {
      return [];
    }

    return questionMap[questionName] || questionMap['default'];
  }

  // utility: validation
  getRequired(item: any) {
    // custom form types and names
    if (item.QuestionType === 'Compound'
      || item.IsOptional
      || ((item.validators != null) ? (Object.values(item.validators).indexOf('required') < 0) : false)
      || customFormTypes.includes(item.QuestionType)
      || item.MultipleResponse
      || skippedQuestions.includes(item.QuestionName)
      || item.ConditionalClause === '0'
    ) {
      return false;
    } else {
      return true;
    }
  }

  // utility: add allocation for beneficiaries & owners
  addAllocationQuestion(form: FormGroup, controlName?: string) {
    if (controlName !== '') {
      if(controlName=='totalOwners'){
        return form.addControl(controlName, this.builder.control(0, [Validators.required, validateOwnerAmount]));
      } else {
        return form.addControl(controlName, this.builder.control(0, [Validators.required, validatePercentageAllocation]));
      }
    }
    return form.addControl('totalAllocation', this.builder.control(0, [Validators.required, validatePercentageAllocation]));
  }

  // utility: allocation total
  calculateTotalAllocation(formValues: Array<any>, fieldName: any) {
    // get values
    const values = map(formValues, (item: any) => parseInt(item[fieldName], 10) || 0);
    // sum
    const total = reduce(values, (sum: any, n: any) => {
      return sum + n;
    }, 0);

    return total;
  }

  calculateTotalAllocationType(formValues: Array<any>, fieldName: any, type: string) {
    const matchingValues = formValues.filter((value) => {
      return value['BeneficiaryPrimaryOrSecondary'] == type;
    });

    // values
    const values = map(matchingValues, (item: any) => parseInt(item[fieldName], 10) || 0);

    // sum
    const total = reduce(values, (sum: any, n: any) => {
      return sum + n;
    }, 0);

    return total;
  }

  // utility: filter skipped questions
  filterSkippedQuestions(questions: QuestionBase<any>[]) {
    const filtered = questions.filter((item) => {
      // omitting children of suppressed compounds
      const parent = find(questions, { QuestionName: item.ParentQuestionName });
      const parentBlock = parent ? parent.ConditionalClause === '0' : false;

      return item.ConditionalClause !== '0' && !parentBlock;
    });

    return filtered;
  }

  checkIfEditable(permissions: any, user: any, isDemo: boolean): boolean {
    // if (isDemo) {
    //   return true;
    // }
    // bypass permissions if not set in app definition or userRole is 'Omnipotent'
    const roleStages = new Map([
      ["InsuredOwner", 2.1],
      ["InsuredNotOwner", 2.2],
      [ "OwnerA", 3 ],
      [ "OwnerB", 4 ],
      [ "OwnerC", 5 ],
      [ "OwnerD", 6 ],
      [ "OwnerE", 7 ],
      [ "OwnerF", 8 ],
      [ "OwnerG", 9 ],
      [ "OwnerH", 10 ],
      [ "OwnerI", 11 ],
      [ "OwnerJ", 12 ]
    ]);
    if (!permissions || user.name == 'Omnipotent') {
      return true;
    }

    // default stage for Agent's first pass
    let stage: number = 1;
    if (user.name == 'Agent' && user.customerSigned == 1) {
      // set stage for Agent's final pass
      stage = 13;
    } else if (user.name.includes('Owner') && user.customerSigned == 1 || user.name.includes('Insured')) {
      // set stage for Agent's final pass
      //console.log('i am owner: ', user.name);
      stage = roleStages.get(user.name);
    }

    // process permissions
    permissions = filter(permissions, { Stage: stage });
    permissions = permissions.map((item: any) => {
      return item.QuestionBlockPermissionType;
    });
    return permissions.indexOf('Write') >= 0 ? true : false;
  }
  checkIfViewable(permissions: any, user: any, isDemo: boolean): boolean {
    // if (isDemo) {
    //   return true;
    // }
    // bypass permissions if not set in app definition or userRole is 'Omnipotent'
    const roleStages = new Map([
      ["InsuredOwner", 2.1],
      ["InsuredNotOwner", 2.2],
      [ "OwnerA", 3 ],
      [ "OwnerB", 4 ],
      [ "OwnerC", 5 ],
      [ "OwnerD", 6 ],
      [ "OwnerE", 7 ],
      [ "OwnerF", 8 ],
      [ "OwnerG", 9 ],
      [ "OwnerH", 10 ],
      [ "OwnerI", 11 ],
      [ "OwnerJ", 12 ]
    ]);
    if (!permissions || user.name == 'Omnipotent') {
      //console.log('no permissions!');
      return true;
    }

    // default stage for Agent's first pass
    let stage: number = 1;
    if (user.name == 'Agent' && user.customerSigned == 1) {
      // set stage for Agent's final pass
      stage = 13;
    } else if ((user.name.includes('Owner') && user.customerSigned == 1) || user.name.includes('Insured')) {
      // find correct owner or insured role
     // console.log('i am owner: ', user.name);
      stage = roleStages.get(user.name);
    }
    //console.log('stage: ', stage);
    // process permissions
    permissions = filter(permissions, { Stage: stage });
    permissions = permissions.map((item: any) => {
      return item.QuestionBlockPermissionType;
    });
   // console.log('permissions: ', permissions);
    return permissions.indexOf('Write') >= 0 ? true : permissions.indexOf('Read') >= 0 ? true : false;
  }

  filterEnabledQuestions(questions: QuestionBase<any>[], user: any, isDemo: boolean) {
    const returners = [];
    questions.forEach(x => {
      if (Object.keys(x.QuestionRestrictions).length > 0) {
        forEach(x.QuestionRestrictions, (value, key)=> {
          if (value.user == user.name) {
            if (!isDemo) {
              returners.push(x.QuestionName);
            } else if (isDemo && (
              x.QuestionType !== 'Name' &&
              x.QuestionType !== 'Phone' &&
              x.QuestionType !== 'SSN' && x.QuestionName !== 'BeneficiarySSN'&&
              x.QuestionName !== 'PolicyOwnerTaxId' &&
              x.QuestionName !== 'BeneficiaryTaxID' &&
              x.QuestionName !== 'TINEmployerID' &&
              !x.QuestionName.includes('Email') &&
              !x.QuestionText.includes('Email Address') &&
              x.inputType !== 'email' &&
              x.QuestionName !== 'DriversLicense' &&
              x.QuestionName !== 'DriversLicenseNumber'
            )) {
              returners.push(x.QuestionName);
            }
          }
        })
      }
    });

    return returners;
  }

  filterMultiResponseRestrictedQuestions(questions: QuestionBase<any>[], userRole: string, isDemo: boolean) {
    const returners = [];
    questions.forEach(x => {
      if (Object.keys(x.QuestionRestrictions).length > 0) {
      forEach(x.QuestionRestrictions, (value, key)=> {
          if (value.user == userRole) {
            if (!isDemo) {
              returners.push(x.QuestionName);
            } else if (isDemo && (
              x.QuestionType !== 'Name' &&
              x.QuestionType !== 'Phone' &&
              x.QuestionType !== 'SSN' && x.QuestionName !== 'BeneficiarySSN'&&
              x.QuestionName !== 'PolicyOwnerTaxId' &&
              x.QuestionName !== 'BeneficiaryTaxID' &&
              x.QuestionName !== 'TINEmployerID' &&
              !x.QuestionName.includes('Email') &&
              !x.QuestionText.includes('Email Address') &&
              x.inputType !== 'email' &&
              x.QuestionName !== 'DriversLicense' &&
              x.QuestionName !== 'DriversLicenseNumber'
            )) {
              returners.push(x.QuestionName);
            }
          }
        })
      }
    });
    return returners;
  }

  // prefill with default values
  defaultValues(section: any, userRole: any) {

    let hiddenDefaults = [];

    forEach(section.Questions, question => {
      if (question.DefaultValue) {
        const val = {[question.QuestionName]: question.DefaultValue.replace(/(["\\])/g, '')};
        hiddenDefaults = assign({},hiddenDefaults,val);
      };
      if (question.QuestionName == 'PolicyCreateDate') {
       const date = { ['PolicyCreateDate']: luxon.DateTime.now().toFormat('yyyy-MM-dd').toString() };
        hiddenDefaults = assign({}, hiddenDefaults, date);
      }
    });
    return hiddenDefaults;
  }

  // returnIllustrationPrefilledVals(): Observable<any[]> {
  //   return this.prefillValuesSource.asObservable();
  // }

  // savePrefillVals(newValue: boolean): void {
  //   this.prefillValuesSource = new BehaviorSubject([]);
  //   this.prefillValuesSource.next([...this.prefillValuesSource.value, newValue]);
  // }

  removeIndexedValues(pvulProduct?:any) {
    if (pvulProduct) {
      if (this.vulAccumulationTotals['VULFundIndexedAcctsBCIA']) {
        delete this.vulAccumulationTotals['VULFundIndexedAcctsBCIA'];
      }
      if (this.vulAccumulationTotals['VULFundIndexedAcctsBHPCIA']) {
        delete this.vulAccumulationTotals['VULFundIndexedAcctsBHPCIA'];
      }
      if (this.vulAccumulationTotals['VULFundIndexedOptionsCompound']) {
        delete this.vulAccumulationTotals['VULFundIndexedOptionsCompound'];
      }
    }
  }

  updateAccumulationTotal(questionName: any, total: any, form: any, tableQuestions?: any) {
    this.tableQuestions = tableQuestions ? tableQuestions : this.tableQuestions;
    this.vulAccumulationTotals[questionName] = total;
    this.calculateVULFinalTotal();
    if (this.vulFinalTotal < 100) {
      this.vulAlreadyZeroed = false;
    } else if (!this.vulAlreadyZeroed && this.vulFinalTotal === 100) {
      this.vulAlreadyZeroed = true;
      this.tableQuestions.forEach(q => {
        if (form.controls[q] && (form.controls[q].value == null || form.controls[q].value == '') && q[0] !== 'VULFundManagedVolatilityAggressive') {
          form.controls[q].patchValue('0');
        }
      });

    }
  }
  vulSegmentProceedsAllocationTotal(questionName: any, total: any) {
    this.vulSegmentProceedsAllocationTotals[questionName] = total;
    this.calculateVulSegmentProceedsAllocationTotal();
  }
  BaseHighParCappedIndexedAccountTotal(questionName: any, total: any) {
    this.BaseHighParCappedIndexedAccountTotals[questionName] = total;
    this.calculateBaseHighParCappedIndexedAccountTotal();
  }
  //JIRA: 3476 Begin
  BaseCappedTwoYearIndexedAccountTotal(questionName: any, total: any) {
    this.BaseCappedTwoYearIndexedAccountTotals[questionName] = total;
    this.calculateBaseCappedTwoYearIndexedAccountTotal();
  }
  HighCappedIndexedAccountTotal(questionName: any, total: any) {
    this.HighCappedIndexedAccountTotals[questionName] = total;
    this.calculateHighCappedIndexedAccountTotal();
  }
  //JIRA: 3476 End
  AllocationofMonthlyChargesTotal(questionName: any, total: any) {
    this.AllocationofMonthlyChargesTotals[questionName] = total;
    this.calculateAllocationofMonthlyChargesTotal();
  }
  AccountBalancerTotal(questionName: any, total: any) {
    this.AccountBalancerTotals[questionName] = total;
    this.calculateAccountBalancerTotal();
  }
  getVulValidity() {
    if (this.vulAccumulationTotals)
    {
      if (this.vulFinalTotal > 0 && this.vulFinalTotal !== 100)
      {
        return false;
      }
    }
    if (this.vulSegmentProceedsAllocationTotals)
    {
      if (this.vulSegmentProceedsAllocationFinalTotal > 0 && this.vulSegmentProceedsAllocationFinalTotal !== 100)
      {
        return false;
      }
    }
    if (this.BaseHighParCappedIndexedAccountTotals)
    {
      if (this.BaseHighParCappedIndexedAccountFinalTotal > 0 && this.BaseHighParCappedIndexedAccountFinalTotal !== 100)
      {
        return false;
      }
    }
    //JIRA: 3476 Begin
    if (this.BaseCappedTwoYearIndexedAccountTotals)
    {
      if (this.BaseCappedTwoYearIndexedAccountFinalTotal > 0 && this.BaseCappedTwoYearIndexedAccountFinalTotal !== 100)
      {
        return false;
      }
    }
    if (this.HighCappedIndexedAccountTotals)
    {
      if (this.HighCappedIndexedAccountFinalTotal > 0 && this.HighCappedIndexedAccountFinalTotal !== 100)
      {
        return false;
      }
    }
    //JIRA: 3476 End
    if (this.AllocationofMonthlyChargesTotals)
    {
      if (this.AllocationofMonthlyChargesFinalTotal > 0 && this.AllocationofMonthlyChargesFinalTotal !== 100)
      {
        return false;
      }
    }
    if (this.AccountBalancerTotals)
    {
      if (this.AccountBalancerFinalTotal > 0 && this.AccountBalancerFinalTotal !== 100)
      {
        return false;
      }
    }
    return true;

  }

  calculateVULFinalTotal() {
    this.vulFinalTotal = 0;
    Object.values(this.vulAccumulationTotals).map(val => {
        this.vulFinalTotal += val;
    });
  }
  calculateVulSegmentProceedsAllocationTotal() {
    this.vulSegmentProceedsAllocationFinalTotal = 0;
    Object.values(this.vulSegmentProceedsAllocationTotals).map(val => {
        this.vulSegmentProceedsAllocationFinalTotal += val;
    });
  }
  calculateBaseHighParCappedIndexedAccountTotal() {
    this.BaseHighParCappedIndexedAccountFinalTotal = 0;
    Object.values(this.BaseHighParCappedIndexedAccountTotals).map(val => {
      this.BaseHighParCappedIndexedAccountFinalTotal += val;
    });
  }
  //JIRA: 3476 Begin
  calculateBaseCappedTwoYearIndexedAccountTotal() {
    this.BaseCappedTwoYearIndexedAccountFinalTotal = 0;
    Object.values(this.BaseCappedTwoYearIndexedAccountTotals).map(val => {
      this.BaseCappedTwoYearIndexedAccountFinalTotal += val;
    });
  }
  calculateHighCappedIndexedAccountTotal() {
    this.HighCappedIndexedAccountFinalTotal = 0;
    Object.values(this.HighCappedIndexedAccountTotals).map(val => {
      this.HighCappedIndexedAccountFinalTotal += val;
    });
  }
  //JIRA: 3476 End
  calculateAllocationofMonthlyChargesTotal() {
    this.AllocationofMonthlyChargesFinalTotal = 0;
    Object.values(this.AllocationofMonthlyChargesTotals).map(val => {
      this.AllocationofMonthlyChargesFinalTotal += val;
    });
  }
  calculateAccountBalancerTotal() {
    this.AccountBalancerFinalTotal = 0;
    Object.values(this.AccountBalancerTotals).map(val => {
      this.AccountBalancerFinalTotal += val;
    });
  }

  getFinalTotal() {
    return this.vulFinalTotal;
  }
  getVulSegmentProceedsAllocationTotal() {
    return this.vulSegmentProceedsAllocationFinalTotal;
  }
  getBaseHighParCappedIndexedAccountTotal() {
    return this.BaseHighParCappedIndexedAccountFinalTotal;
  }
  //JIRA: 3476 Begin
  getBaseCappedTwoYearIndexedAccountTotal() {
        return this.BaseCappedTwoYearIndexedAccountFinalTotal;
  }
  getHighCappedIndexedAccountTotal() {
    return this.HighCappedIndexedAccountFinalTotal;
  }
  //JIRA: 3476 End
  getAllocationofMonthlyChargesTotal() {
    return this.AllocationofMonthlyChargesFinalTotal;
  }
  getAccountBalancerTotal() {
    return this.AccountBalancerFinalTotal;
  }

  generateEAppRawPayload(QuestionBlocks, QuestionBlockResponses) {
    console.log('Generate EApp RawPayload');

    // toggles when this block is marked valid based on response provided
    let tempInsuranceBlockValid = true;

    // Initialize RawPayload
    const rawPayload={};
    QuestionBlockResponses.forEach(block => {

      if (block.HasResponse) {
        const blockQuestionAnswers={};
        // Initialize the question blocks / sections
        rawPayload[block.QuestionBlock]=null;

        block.QuestionResponses.forEach(questionResponse => {
          // Add only if answered
          if (questionResponse.HasResponse) {
            // Add question to blockQuestionAnswers
            blockQuestionAnswers[questionResponse.QuestionName] = questionResponse.AnswerText && isString(questionResponse.AnswerText) ? JSON.parse(questionResponse.AnswerText) : questionResponse.AnswerText;

            // Fix field mappings using the AppDefinition
            const sectionDef = find(QuestionBlocks, { Name: block.QuestionBlock });
            const questionDef = find(sectionDef.Questions, { Id: questionResponse.Id });
            if (questionDef) {
              let allQuestionAnswers=[];
              const questionAnswers=[];
              if (questionDef.MultipleResponse && isArray(blockQuestionAnswers[questionResponse.QuestionName])) {
                allQuestionAnswers =blockQuestionAnswers[questionResponse.QuestionName];
              } else {
                allQuestionAnswers = [ blockQuestionAnswers[questionResponse.QuestionName] ];
              }
              // Fix compound questions
              let index=0;
              allQuestionAnswers.forEach(questionAnswer => {
                index++;
                if (questionDef.QuestionType==="Compound" && questionDef.ChildQuestions) {
                  // Find each child question definition
                  questionDef.ChildQuestions.forEach(childQuestion => {
                    // Find question definition
                    const childQuestionDef = find(sectionDef.Questions, { QuestionName: childQuestion.QuestionName, ParentQuestionName: questionDef.QuestionName });
                    // If definition is found
                    if (childQuestionDef) {
                      // Find question answer object
                      if (childQuestionDef.QuestionType==="Compound" && childQuestionDef.ChildQuestions) {
                        childQuestionDef.ChildQuestions.forEach(grandchildQuestion => {
                          // Find question definition
                          const grandchildQuestionDef = find(sectionDef.Questions, { QuestionName: grandchildQuestion.QuestionName, ParentQuestionName: childQuestionDef.QuestionName });
                          if (grandchildQuestionDef) {
                            let grandchildQuestionAnswer = get(questionAnswer, grandchildQuestionDef.QuestionName );
                            grandchildQuestionAnswer = this.getEAPPRawQuestion(grandchildQuestionDef,grandchildQuestionAnswer);
                            set(questionAnswer,grandchildQuestionDef.QuestionName, grandchildQuestionAnswer);
                          }
                        });
                      } else {
                        let childQuestionAnswer = get(questionAnswer, childQuestionDef.QuestionName );
                        // If question answer object is found
                        if (childQuestionAnswer !== undefined && childQuestionAnswer !== null) {
                          childQuestionAnswer = this.getEAPPRawQuestion(childQuestionDef,childQuestionAnswer);
                          set(questionAnswer,childQuestionDef.QuestionName, childQuestionAnswer);
                        }
                      }
                    }

                  });
                  // TODO: Implement recursion
                  questionAnswers.push(questionAnswer);
                } else {
                  questionAnswer = this.getEAPPRawQuestion(questionDef,questionAnswer);
                  questionAnswers.push(questionAnswer);
                }
              });
              if (questionDef.MultipleResponse && isArray(blockQuestionAnswers[questionResponse.QuestionName])) {
                blockQuestionAnswers[questionResponse.QuestionName] =questionAnswers;
              } else {
                blockQuestionAnswers[questionResponse.QuestionName] =questionAnswers[0];
              }
            }
            // toggles valid setting on Temporary Insurance block in following menu data object depending on response to TempAgreementWanted
            if (questionResponse.QuestionName === 'TempAgreementWanted' && questionResponse.AnswerText == 'true') {
              tempInsuranceBlockValid = null;
            }
          }
        });
        // Add blockQuestionAnswers to payload
        rawPayload[block.QuestionBlock]=blockQuestionAnswers;
      }
    });
    // adds hard-coded simplified menuData objet for ASH cases to show correct status on initial load
    rawPayload['menuData'] = [{"label":"Proposed Insured","name":"SECTIONAProposedInsured","path":"express/proposed-insured","valid":null,"visible":true,"questions":[]},{"label":"Policy Owner","name":"SECTIONBPolicyOwner","path":"express/policy-owner","valid":true,"visible":true,"questions":[]},{"label":"Partnership Statement","name":"PartnershipStatement","path":"express/partnership-statement","valid":null,"visible":false,"questions":[],"conditionalClause":[["SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"Business\"}","OR","SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerRelationship == \"BusinessPartner\"}"],"AND",["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsuredNo == TRUE"]]},{"label":"Trust Certification","name":"TrustCertification","path":"express/trust-certification","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"ExistingTrust\"}"]},{"label":"Beneficiary Information","name":"SECTIONCBeneficiaryInformation","path":"express/beneficiary-information","valid":true,"visible":true,"questions":[]},{"label":"Coverage Details","name":"SECTIONDCoverageDetails","path":"express/coverage-details","valid":null,"visible":true,"questions":[]},{"label": "LTCR Personal Worksheet", "name": "LTCRPersonalWorksheet", "path": "express/ltc-personal-worksheet", "valid": null, "visible": false, "questions": [], "conditionalClause": ["SECTIONAProposedInsured.StateOfIssue == \"PA\"", "OR","SECTIONAProposedInsured.StateOfIssue == \"TX\"", "OR","SECTIONAProposedInsured.StateOfIssue == \"UT\"", "AND","SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"LongTermCareRider\"","AND","SECTIONAProposedInsured.AgentAttestation != TRUE"]},{"label":"Pre-Auth Payment","name":"RequestforPreAuthorizedPaymentPlan","path":"express/pre-authorized-payment","valid":true,"visible":true,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.BillingMethod == \"PreAuthorizedPaymentPlan\""]},{"label":"Critical Illness Rider","name":"CriticalIllness","path":"express/critical-illness","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"CriticalIllnessBenefitRider\""]},{"label":"IUL Allocations","name":"IULPremandSegmentProceedsAllocations","path":"express/proceeds-allocation","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.ProductName CONTAINS \"IUL\"","OR","SECTIONDCoverageDetails.ProductName CONTAINS \"Indexed\""]},{"label":"VUL Allocations","name":"VULFundAllocation","path":"express/vul-fund-allocation","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.ProductName CONTAINS \"VUL\"","OR","SECTIONDCoverageDetails.ProductName CONTAINS \"Variable\""]},{"label":"Statement Policy Cost","name":"SupplementCostBenefitPA","path":"express/supplement-cost-benefit-pa","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONAProposedInsured.StateOfIssue == \"PA\"","AND","SECTIONDCoverageDetails.ProductName CONTAINS \"Term\"","AND","SECTIONAProposedInsured.PartnerID != \"Ash001\"","AND","SECTIONAProposedInsured.PartnerID != \"Ebix001\"","AND","SECTIONAProposedInsured.PartnerID != \"Vive001\""]},{"label":"Statement Policy Cost","name":"SupplementCostBenefitME","path":"express/supplement-cost-benefit-me","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONAProposedInsured.StateOfIssue == \"ME\"","AND","SECTIONDCoverageDetails.ProductName CONTAINS \"Term\"","AND","SECTIONAProposedInsured.PartnerID != \"Ash001\"","AND","SECTIONAProposedInsured.PartnerID != \"Ebix001\"","AND","SECTIONAProposedInsured.PartnerID != \"Vive001\""]},{"label":"Illustration","name":"IllustrationCertification","path":"express/illustration-certification","valid":true,"visible":false,"questions":[],"conditionalClause":["SECTIONAProposedInsured.PartnerID != \"Ash001\"","AND","SECTIONAProposedInsured.PartnerID != \"Ebix001\"","AND","SECTIONAProposedInsured.PartnerID != \"Vive001\""]},{"label":"Purpose & Funding","name":"SECTIONEPurposeFundingInformation","path":"express/purpose-funding-information","valid":true,"visible":true,"questions":[]},{"label":"Existing Insurance","name":"SECTIONFExistingReplacementPendingInsuranceInformation","path":"express/existing-replacement-pending","valid":true,"visible":true,"questions":[]},{"label":"Temporary Insurance","name":"SECTIONJTemporaryLifeInsuranceAgreementApplication","path":"express/temporary-life-insurance","valid":tempInsuranceBlockValid,"visible":true,"questions":[]},{"label":"Juvenile Insurance","name":"SECTIONIJuvenileInsurance","path":"express/juvenile-insurance","valid":null,"visible":false,"questions":[],"conditionalClause":["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) < 18"]},{"label":"Business Financial Supplement","name":"FinancialSupplementforBusinessInsurance","path":"express/supplement-financial-business","valid":null,"visible":true,"questions":[],"conditionalClause":["SECTIONEPurposeFundingInformation.PurposeOfInsurance CONTAINS \"BusinessInsurance\"","OR","SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"Business\"}","OR","SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerRelationship IN [\"BusinessPartner\", \"Employer\"]}","OR","SECTIONCBeneficiaryInformation.BeneficiaryInformation.ANY{BeneficiaryRelationship IN [\"BusinessPartner\", \"Employer\"]}"]},{"label":"Taxpayer ID ","name":"TIN","path":"express/taxpayer-identification","valid":null,"visible":true,"questions":[]},{"label":"Personal Financial Supplement","name":"SupplementFinancialPersonal","path":"express/supplement-financial-personal","valid":null,"visible":false,"questions":[],"conditionalClause":[["!(SECTIONEPurposeFundingInformation.PurposeOfInsurance CONTAINS \"BusinessInsurance\"","OR","SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerType == \"Business\"}","OR","SECTIONBPolicyOwner.PolicyOwnerInformation.ANY{PolicyOwnerRelationship IN [\"BusinessPartner\", \"Employer\"]}","OR","SECTIONCBeneficiaryInformation.BeneficiaryInformation.ANY{BeneficiaryRelationship IN [\"BusinessPartner\", \"Employer\"]}"],"AND",[["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) <= 65","AND","SECTIONDCoverageDetails.FlexiblePremiumProductsBaseAmount + SECTIONDCoverageDetails.FlexiblePremiumProductsSupplementalAmount + SECTIONDCoverageDetails.TermFaceAmount) > 7500000"],"OR",["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) >= 66","AND","AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) <= 79","AND","SECTIONDCoverageDetails.FlexiblePremiumProductsBaseAmount + SECTIONDCoverageDetails.FlexiblePremiumProductsSupplementalAmount + SECTIONDCoverageDetails.TermFaceAmount) >= 5000000"],"OR",["AGE_NEAREST(SECTIONAProposedInsured.DateofBirth) >= 80","AND","SECTIONDCoverageDetails.FlexiblePremiumProductsBaseAmount + SECTIONDCoverageDetails.FlexiblePremiumProductsSupplementalAmount + SECTIONDCoverageDetails.TermFaceAmount) >= 1000000)"]]]},{"label":"Personal Information","name":"SECTIONGPersonalInformation","path":"express/personal-information","valid":null,"visible":true,"questions":[]},{"label":"Lifestyle Information","name":"SECTIONHLifestyleInformation","path":"express/lifestyle-information","valid":null,"visible":true,"questions":[]},{"label":"Avocation Questionnaire","name":"SupplementAvocation","path":"express/supplement-avocation","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONHLifestyleInformation.EngagedActivity NOT CONTAINS \"NoParticipation\""]},{"label":"Aviation Questionnaire","name":"SupplementAviation","path":"express/supplement-aviation","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONHLifestyleInformation.Pilot == TRUE"]},{"label":"Medical Overview","name":"Part2SECTIONAGeneralInformation","path":"express/part2-general-information","valid":null,"visible":true,"questions":[]},{"label":"Medications","name":"Part2SECTIONBMedications","path":"express/part2-medications","valid":null,"visible":true,"questions":[]},{"label":"Medical Conditions","name":"Part2SECTIONCMedicalConditions","path":"express/part2-medical-conditions","valid":null,"visible":true,"questions":[]},{"label":"Medical Condition Details","name":"Part2SECTIONCMedicalConditions2","path":"express/part2-medical-conditions-details","valid":null,"visible":false,"questions":[],"conditionalClause":["Part2SECTIONCMedicalConditions.HeartOrBloodVessel NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.EndocrineOrDiabetes NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.Cancer NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.BloodOrAutoimmuneDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.RespiratoryDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.NeurologicalDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.MentalHealthDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.GastrointestinalDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.BoneJointDisorder NOT CONTAINS \"NoneOfTheseApplyToMe\"","OR","Part2SECTIONCMedicalConditions.KidneyBladderBreastReproductive NOT CONTAINS \"NoneOfTheseApplyToMe\""]},{"label":"Medical Diagnostics","name":"Part2SECTIONDMedicalDiagnostic","path":"express/part2-medical-diagnostic","valid":null,"visible":true,"questions":[]},{"label":"Medical Personal Info","name":"Part2SECTIONEPersonalInformation","path":"express/part2-personal-information","valid":null,"visible":true,"questions":[]},{"label":"Alcohol and Drugs","name":"SupplementAlcoholAndDrug","path":"express/supplement-alcohol-and-drug","valid":null,"visible":false,"questions":[],"conditionalClause":["Part2SECTIONEPersonalInformation.AlcoholAdvisedDiscontinueOrTreatment == TRUE","OR","Part2SECTIONEPersonalInformation.DrugsCounselingTreatment == TRUE","OR","Part2SECTIONEPersonalInformation.CocaineHeroinAmphetaminesHallucinogens == TRUE","OR","Part2SECTIONEPersonalInformation.TranquilizersSedativesNarcoticsPrescriptions == TRUE"]},{"label":"LTC Rider Supplement","name":"SupplementLTC","path":"express/supplement-long-term-care","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"LongTermCareRider\"","AND","SECTIONAProposedInsured.StateOfIssue != \"CA\""]},{"label":"Chronic Illness Rider Supplement","name":"SupplementDBCHR","path":"express/supplement-dbchr","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"DefinedBenefitChronicIllness\""]},{"label":"LTC Rider Supplement","name":"SupplementLTCCA","path":"express/supplement-long-term-care-ca","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.PermRidersAndBenefits CONTAINS \"LongTermCareRider\"","AND","SECTIONAProposedInsured.StateOfIssue == \"CA\""]},{"label":"Pre-Auth Payment","name":"RequestforPreAuthorizedPaymentPlanInsured","path":"express/pre-authorized-payment-plan","valid":null,"visible":true,"questions":[],"conditionalClause":["SECTIONDCoverageDetails.BillingMethod == \"PreAuthorizedPaymentPlan\""]},{"label":"Disclosures","name":"Disclosures","path":"express/disclosures","valid":null,"visible":false,"questions":[]},{"label":"Insured Declarations","name":"DeclarationsInsured","path":"express/declarations","valid":null,"visible":false,"questions":[]},{"label":"Owner Declarations","name":"DeclarationsOwnerA","path":"express/declarations-owner-A","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerB","path":"express/declarations-owner-B","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerC","path":"express/declarations-owner-C","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerD","path":"express/declarations-owner-D","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerE","path":"express/declarations-owner-E","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerF","path":"express/declarations-owner-F","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerG","path":"express/declarations-owner-G","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerH","path":"express/declarations-owner-H","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerI","path":"express/declarations-owner-I","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Owner Declarations","name":"DeclarationsOwnerJ","path":"express/declarations-owner-J","valid":null,"visible":false,"questions":[],"conditionalClause":["SECTIONBPolicyOwner.PolicyOwnerInsured == \"CoOwner\"","OR","SECTIONBPolicyOwner.PolicyOwnerInsured == \"No\""]},{"label":"Agent Report","name":"AgentReport","path":"express/agent-report","valid":true,"visible":false,"questions":[]},{"label":"Transmittal","name":"TransmittalBlock","path":"express/transmittal","valid":true,"visible":false,"questions":[]},{"label":"Agent Declarations","name":"DeclarationsAgent","path":"express/declarations-agent","valid":false,"visible":false,"questions":[]}];
    return rawPayload;
  }

  private getEAPPRawQuestion(questionDef, questionAnswer) {

    switch (questionDef.QuestionType) {
      case "Name":
        if (questionAnswer) {
            if (questionAnswer.Title) {
              questionAnswer.Title =questionAnswer.Title + '.';
            }
            if (questionAnswer.Suffix && (questionAnswer.Suffix==='Jr' || questionAnswer.Suffix==='Sr' )) {
              questionAnswer.Suffix=questionAnswer.Suffix + '.';
            }
          }
        break;

		  case "Date":
        if (questionAnswer) { // And is a valid
         //  questionAnswer = luxon.DateTime.fromJSDate(new Date(questionAnswer.replace(/-/g, '\/'))).toFormat('MM-dd-yyyy').toString();
        questionAnswer = luxon.DateTime.fromJSDate(new Date(questionAnswer.replace(/-/g, '\/'))).toFormat('MM/dd/yyyy').toString(); //New Date format 'MM/dd/yyyy'
        }
        break;

      case "YesNo":
        if (questionAnswer === true) {
          questionAnswer= 'true';
        }
        else if (questionAnswer === false) {
          questionAnswer= 'false';
        }
        break;
      case "Length":
        if (questionAnswer && questionAnswer.Feet && questionAnswer.Inches) {
          questionAnswer.Inches = questionAnswer.Inches.toString();
        }
        break;
      case "Integer":
          if (questionAnswer && questionDef.Units && questionDef.Units==='lbs') {
            questionAnswer = questionAnswer.toString();
          }
          break;

      case "MultiSelect":
        const selection = [];
        if (questionAnswer) {
          //var option = isArray(questionAnswer) && questionAnswer.length >0 ? questionAnswer[0] : questionAnswer;
          if (isArray(questionAnswer) && questionAnswer.length >0 ) {
            console.log('questionAnswer:',questionAnswer);
            questionDef.QuestionOptions.forEach(questionOption=>{

                console.log('questionDef.QuestionName:',questionDef.QuestionName);
                console.log('questionDef.QuestionOptions:',questionDef.QuestionOptions);
                const option =questionAnswer.find(opt => opt !=undefined && questionOption.Name === opt.toString());
               // questionAnswer.forEach(option => {

                  console.log('option:',option);
                  const otionSelected=false;
                  if (option && option !=undefined) {
                    if (questionOption.ConditionalClause) {
                      selection.push(questionOption.Name===option.toString());
                    } else {
                      selection.push(questionOption.Name===option.toString());
                    }
                  } else if (questionOption.IsExclusive) {
                    selection.push(false);
                  } else {
                    selection.push(null);
                  }
              });
              questionAnswer =  selection;
          }

        }
        break;

      case "MonthYear":
        if (questionAnswer && questionAnswer.Month && questionAnswer.Year) {
          const newDate =new Date(parseInt(questionAnswer.Year),(parseInt(questionAnswer.Month) -1));
          questionAnswer = luxon.DateTime.fromJSDate(newDate).toFormat('MM/yyyy').toString(); //New Date format 'MM/dd/yyyy'
        }
        break;

      default:
          break;
    }
    return  questionAnswer;
  }

/**
 * Traverses deeply nested JSON objects
 * @param theObject
 * @returns
 */
deepParseObject(theObject) {
  for(var prop in theObject) {
      if(typeof theObject[prop] === "string" && (theObject[prop].indexOf('{') == 0 || theObject[prop].indexOf('[') == 0)) {
          theObject[prop] = JSON.parse(theObject[prop]);
      }
      if(theObject[prop] instanceof Object || theObject[prop] instanceof Array)
          this.deepParseObject(theObject[prop]);
  }
  return theObject;
}

/**
 * Retrieves nested question response answers
 * @param response
 * @param questionDef
 * @param sectionDef
 * @returns
 */
getChildResponses(response:any, questionDef:any, sectionDef:any)
{

  if(response !== null && typeof response !== 'undefined') {

    let i = 0;

    const children = Array.from({ length: response.length ?? 0 },()=>({}))

    response.forEach(arr => {

      Object.keys(arr).forEach(key => {

        const childDef = find(sectionDef.Questions, {
          ParentQuestionName: questionDef.QuestionName,
          QuestionName: key
        }) ?? find(sectionDef.Questions, {
          QuestionName: key
        });

        if(childDef.ChildQuestions !== null && typeof childDef !== 'undefined') {
          set(children[i], key, this.getChildResponses(arr[key], childDef, sectionDef));
        } else {
          const corresponding = (typeof childDef !== 'undefined' && childDef !== null) ?
          this.getEAPPRawQuestion(childDef, arr[key]) : this.getEAPPRawQuestion(questionDef, arr[key]);
          set(children[i], key, corresponding);
        }

      });

      i++;
    });

    return children;
  }

}

/**
 * Sets a key-value pair target
 * @param target
 * @param key
 * @param val
 */
setQuestionBlockAnswer(target:any, key:string, val:any)
{
  try {
    set(target, key, val)
  } catch(e) {
    set(target, key, null)
  }
}

/**
 * Returns value based on key from a given target
 * @param target
 * @param key
 * @param child
 * @returns array[key] value
 */
getQuestionBlock(target: any, key:string)
{
  try {
    return get(target, key)
  } catch(e) {
    set(target, key, null)
    return get(target, key)
  }
}

/**
 * Interprets a given question block response
 * @param response
 * @param questionDef
 * @param sectionDef
 * @param blockName
 */
interpret(response:any,questionDef:any, sectionDef:any, payload)
{
  if(response !== null && typeof response !== 'undefined' && response.hasOwnProperty('AnswerText')) {

    const formatted = (isString(response.AnswerText)) ? JSON.parse(response.AnswerText) : response.AnswerText;

    if(Array.isArray(formatted)) {
      if(questionDef.ChildQuestions !== null) {
        const temp = this.getChildResponses(formatted, questionDef, sectionDef);
        this.setQuestionBlockAnswer(payload, questionDef.QuestionName, temp);
      } else {
        const temp = this.getEAPPRawQuestion(questionDef, formatted);
        this.setQuestionBlockAnswer(payload, questionDef.QuestionName, temp);
      }
    } else {
      const temp = this.getEAPPRawQuestion(questionDef, formatted);
      this.setQuestionBlockAnswer(payload, questionDef.QuestionName, temp);
    }
  } else {
    const temp = this.getEAPPRawQuestion(questionDef, response);
    this.setQuestionBlockAnswer(payload, questionDef.QuestionName, temp);
  }
}

/**
 * generates eApp raw payload
 * @param questionBlocks
 * @param questionResponses
 * @returns this.payload:any
 */
generateRawPayload(questionBlocks, questionResponses) {
  console.log('Forming eApp raw payload...');

  questionResponses.forEach( block => {

    if(block.HasResponse) {

      set(this.payload, block.QuestionBlock, {});

      const payload = get(this.payload, block.QuestionBlock);

      block.QuestionResponses.forEach( questionResponse => {

          if(questionResponse.HasResponse) {
            const sectionDef = find(questionBlocks, {
              Name: block.QuestionBlock
            });
            const questionDef = find(sectionDef.Questions, {
              Id: questionResponse.Id
            });
            const formatted = this.deepParseObject(questionResponse);
            this.interpret(formatted, questionDef, sectionDef, payload)
          }

        });

        set(this.payload, block.QuestionBlock, payload)

      }

    });
    this.setQuestionBlockAnswer(this.payload, 'menuData', this.menuData);

    console.log('Payload: ', this.payload);

    return this.payload;
  }

}

